import { put, takeLatest, call } from 'redux-saga/effects'
import { SignaturesConstants } from '../store/signatures.constants'
import { select } from '@redux-saga/core/effects'
import { ISignaturesState } from '../store/signatures.types'
import { getPcrIdSelector } from '../../home/selectors'
import { getSignaturesSelector } from '../selectors/getSignatures.selector'
import {
  setAmbulanceCrewSignature,
  setRepresentativeSignature,
  setPatientSignature,
  setWitnessSignature,
  setCrewSignature,
  setReceivingFacilitySignature,
  setRepresentativeSignatureDt,
  setAmbulanceSignatureDt,
  setCrewSignatureDt,
  setPatientSignatureDt,
  setReceivingFacilitySignatureDt,
  setWitnessSignatureDt,
  setSignatureChips,
  setSignatureSection,
  saveAttachment,
} from '../store/signatures.actions'
import { getConfigurationsSelector } from '../../configurations/selectors/configurations.selector'
import { IConfigurationsState } from '../../configurations/store/configurations.types'
import { getCurrentDateTimeByTimeZone } from '../../../utilities/dateTime.util'
import { ConfigurationsConstants } from '../../configurations/store/configurations.constants'
import { blobToBase64 } from '../../../utilities/index'
import { ISignatureChip } from '../store/signatures.types'

export function* saveSignatures() {
  const signatureState: ISignaturesState = yield select(getSignaturesSelector)
  const { selectedSignatureType, currentSignature, chips, signatureSection } = signatureState
  const configurationsState: IConfigurationsState = yield select(getConfigurationsSelector)
  const { timeZone } = configurationsState
  const pcrID: string = yield select(getPcrIdSelector)

  if (!pcrID) {
    return
  }

  const dateTime = getCurrentDateTimeByTimeZone(timeZone?.name || ConfigurationsConstants.DEFAULT_TIMEZONE)
  const url = URL.createObjectURL(currentSignature)

  if (selectedSignatureType?.signatureType === SignaturesConstants.PATIENT) {
    if (signatureState.patientSignature) URL.revokeObjectURL(signatureState.patientSignature)
    signatureState.patientSignatureDt = dateTime
    yield put(setPatientSignature(url))
    yield put(setPatientSignatureDt(dateTime))
  } else if (selectedSignatureType?.signatureType === SignaturesConstants.WITNESS) {
    if (signatureState.witnessSignature) URL.revokeObjectURL(signatureState.witnessSignature)
    signatureState.witnessSignatureDt = dateTime
    yield put(setWitnessSignature(url))
    yield put(setWitnessSignatureDt(dateTime))
  } else if (selectedSignatureType?.signatureType === SignaturesConstants.AMBULANCE) {
    if (signatureState.ambulanceCrewSignature) URL.revokeObjectURL(signatureState.ambulanceCrewSignature)
    signatureState.ambulanceSignatureDt = dateTime
    yield put(setAmbulanceCrewSignature(url))
    yield put(setAmbulanceSignatureDt(dateTime))
  } else if (selectedSignatureType?.signatureType === SignaturesConstants.REPRESENTATIVE) {
    if (signatureState.representativeSignature) URL.revokeObjectURL(signatureState.representativeSignature)
    signatureState.representativeSignatureDt = dateTime
    yield put(setRepresentativeSignature(url))
    yield put(setRepresentativeSignatureDt(dateTime))
  } else if (selectedSignatureType?.signatureType === SignaturesConstants.CREW) {
    if (signatureState.crewSignature) URL.revokeObjectURL(signatureState.crewSignature)
    signatureState.crewSignatureDt = dateTime
    yield put(setCrewSignature(url))
    yield put(setCrewSignatureDt(dateTime))
  } else if (selectedSignatureType?.signatureType === SignaturesConstants.RECEIVING) {
    if (signatureState.receivingFacilitySignature) URL.revokeObjectURL(signatureState.receivingFacilitySignature)
    signatureState.receivingFacilitySignatureDt = dateTime
    yield put(setReceivingFacilitySignature(url))
    yield put(setReceivingFacilitySignatureDt(dateTime))
  }

  /* chips creation logic */
  const chipsData = [...chips]
  const base64Data: string = yield call(blobToBase64, currentSignature)
  const currentData: ISignatureChip = {
    signatureBase64: base64Data,
    signatureBlob: url,
    ...signatureSection,
    option: selectedSignatureType,
  }
  const elementIndex = chipsData && chipsData.findIndex((item) => item.option?.id === currentData.option?.id)
  if (elementIndex !== -1) {
    chipsData[elementIndex].signatureBlob = url
    chipsData[elementIndex].signatureBase64 = base64Data
  } else {
    chipsData.push(currentData)
  }
  yield put(setSignatureChips(chipsData))
  yield put(saveAttachment())
  yield put(setSignatureSection(null))

  // ------------------------------------------------------
  // Mike M. removed this call for the following reasons:
  // 1) It's not needed since the signature state is being saved into the PCR in saveAttachment saga
  // 2) There's a race condition that will cause a dirty read (in the saveSignaturesPcrSaga) whereby a write of the data w/o the _attachments can happen before
  //    they have been written in this saga
  //
  // yield put(saveSignaturesPcr())
  //
  // Note: The fix to the DatabaseUtilityService (to actually use a singleton) caused this race condition to surface
  // (This was all done prior to 4.0 regression)
  // ------------------------------------------------------
}

export function* saveSignaturesSaga() {
  yield takeLatest([SignaturesConstants.SAVE_SIGNATURES], saveSignatures)
}
